import React, { useState, useEffect, Fragment } from 'react';
import { withTranslation } from 'react-i18next';
import { reduxForm, Field } from 'redux-form';
import PropTypes from 'prop-types';
import format from 'date-fns/format';
import { ButtonToolbar, Button } from 'reactstrap';
import MatTable from '../../../shared/components/material-table';
import GraphQL from '../../../GraphQL';
import validate from './Validate';
import renderField from '../../../shared/components/form/RenderField';
import DateField from '../../../shared/components/form/DateField';
import HourField from '../../../shared/components/form/HourField';
import { studentListByFilter } from '../../../shared/queries/Student';
import { medicationListByStudent } from '../../../shared/queries/Medication';
import renderDropZoneField from '../../../shared/components/form/DropZone';
import {
  formatYear,
  formatMonth,
  formatDays,
} from '../../../shared/utils/smartutils';
import ColumnsHistoric from './ColumnsHistoric';
import { showNotificationAutoDestroy } from '../../../shared/components/ShowNotification';
import { findSchool } from '../../../shared/queries/SchoolData';
import materialTableIcons from '../../../shared/utils/materialTableIcons';
import SelectVirtualized from '../../../shared/components/form/SelectVirtualized';
import { ACTION_NAME } from '../../../shared/utils/constants';

const MedicationForm = (props) => {
  const {
    t,
    handleSubmit,
    dto,
    initialize,
    showLoading,
    hideLoading,
    backHandler,
    change,
  } = props;
  const [startDate, setStartDate] = useState(new Date());
  const [finalDate, setFinalDate] = useState(new Date());
  const [historicalData, setHistoricalData] = useState([]);
  const [students, setStudents] = useState([]);

  function isDisabledForm() {
    return dto.id;
  }

  function loadStudents() {
    const filter = {
      id: 0,
      classes: [],
      responsibles: [],
      menus: [],
      onlyActive: true,
    };
    showLoading();
    const query = studentListByFilter(filter);
    GraphQL(query, {})
      .then((res) => {
        if (res.studentListByFilter) {
          const loadedStudents = res.studentListByFilter.map((item) => ({
            ...item,
            value: item.id,
            label: item.fullName.concat(
              ' - ',
              formatYear(item.dateOfBirth, t),
              formatMonth(item.dateOfBirth, t),
              formatDays(item.dateOfBirth, t)
            ),
          }));
          setStudents(loadedStudents);
          hideLoading();
        }
      })
      .catch(() => {
        hideLoading();
        showNotificationAutoDestroy(
          'danger',
          t('errors.title'),
          t('errors.error-to-loading')
        );
      });
  }

  function loadHistoricalData(studentId) {
    if (studentId) {
      showLoading();
      const query = medicationListByStudent(studentId);
      GraphQL(query, {})
        .then((res) => {
          if (res.medicationListByFilter) {
            const loadedHistoricalData = res.medicationListByFilter.map(
              (item) => ({
                ...item,
                formattedinitialDate: format(
                  new Date(item.initialDate),
                  'dd/MM/yyyy'
                ),
                formattedfinalDate: format(
                  new Date(item.finalDate),
                  'dd/MM/yyyy'
                ),
                period: format(new Date(item.initialDate), 'dd/MM/yyyy').concat(
                  ' - ',
                  format(new Date(item.finalDate), 'dd/MM/yyyy')
                ),
              })
            );
            setHistoricalData(loadedHistoricalData);
          }
          hideLoading();
        })
        .catch(() => {
          hideLoading();
          showNotificationAutoDestroy(
            'danger',
            t('notifications.error'),
            t('errors.error-to-loading')
          );
        });
    } else {
      setHistoricalData([]);
    }
  }

  const onStudentChangeHandler = (student) => {
    loadHistoricalData(student.id);
  };

  const loadSchoolHours = () => {
    showLoading();
    const query = findSchool();
    GraphQL(query, {})
      .then((res) => {
        change('startTime', res.findSchool.startTime);
        change('finalTime', res.findSchool.finalTime);

        hideLoading();
      })
      .catch(() => {
        hideLoading();
        showNotificationAutoDestroy(
          'danger',
          t('notifications.error'),
          t('errors.error-to-loading')
        );
      });
  };

  const onRecipeItemClickHandler = (evt, rowData) => {
    window.open(rowData.photoRecipeUrl);
  };

  useEffect(() => {
    initialize(dto);
    if (dto.photoRecipeUrl) {
      dto.files = [
        {
          preview: dto.photoRecipeUrl,
          type: dto.photoRecipeMimeType,
          fileId: dto.fileId,
        },
      ];
    }
    if (dto.student && dto.student.id) {
      loadHistoricalData(dto.student.id);
    }
  }, [dto]);

  const actionsDisabledByConditional = (propss) => {
    if (propss.actions && propss.actions.length) {
      const copyProps = { ...propss };
      copyProps.actions.find(
        (a) => a.name === ACTION_NAME.VIEW_MEDICATION_RECIPE
      ).hidden = !copyProps.data.photoRecipeUrl;
    }
  };

  useEffect(() => {
    if (startDate > finalDate) {
      setFinalDate(startDate);
      change('finalDate', startDate);
    }
  }, [startDate]);

  useEffect(() => {
    loadStudents();
    loadSchoolHours();
  }, []);

  return (
    <Fragment>
      <form className="form" onSubmit={handleSubmit}>
        <div className="form__form-group">
          <span className="form__form-group-label">{t('general.student')}</span>
          <div className="form__form-group-field">
            <Field
              name="student"
              type="text"
              component={SelectVirtualized}
              onChange={onStudentChangeHandler}
              placeholder={t('general.student')}
              options={students}
              isDisabled={isDisabledForm()}
            />
          </div>
        </div>
        <div className="form__form-group">
          <span className="form__form-group-label">
            {t('mnt_medication.name')}
          </span>
          <div className="form__form-group-field">
            <Field
              name="name"
              type="text"
              component={renderField}
              placeholder={t('mnt_medication.name')}
              isDisabled={isDisabledForm()}
            />
          </div>
        </div>
        <div className="form__form-group">
          <span className="form__form-group-label">
            {t('mnt_medication.dosage')}
          </span>
          <div className="form__form-group-field">
            <Field
              name="dosage"
              type="text"
              component={renderField}
              placeholder={t('mnt_medication.dosage')}
              isDisabled={isDisabledForm()}
            />
          </div>
        </div>
        <div className="form__form-group">
          <span className="form__form-group-label">
            {t('mnt_medication.routeAdministration')}
          </span>
          <div className="form__form-group-field">
            <Field
              name="routeAdministration"
              type="text"
              component={renderField}
              placeholder={t('mnt_medication.routeAdministration')}
              isDisabled={isDisabledForm()}
            />
          </div>
        </div>
        <div className="form__form-group--2_columns">
          <div className="form__form-group">
            <span className="form__form-group-label">
              {t('general.start-time')}
            </span>
            <div className="form__form-group-field">
              <Field
                name="initialDate"
                type="text"
                component={DateField}
                onChange={(date) => setStartDate(date)}
                placeholder={t('general.start-time')}
                minDate={new Date()}
                disabled={isDisabledForm()}
              />
            </div>
          </div>
          <div className="form__form-group">
            <span className="form__form-group-label">
              {t('general.final-time')}
            </span>
            <div className="form__form-group-field">
              <Field
                name="finalDate"
                type="text"
                component={DateField}
                placeholder={t('general.final-time')}
                minDate={startDate}
                startDate={finalDate}
                disabled={isDisabledForm()}
              />
            </div>
          </div>
        </div>
        <div className="form__form-group--5_columns">
          <div className="form__form-group">
            <span className="form__form-group-label">
              {t('mnt_medication.hour01')}
            </span>
            <div className="date-field">
              <Field
                name="hour01"
                type="text"
                component={HourField}
                placeholder={t('placeholder.hour')}
                isDisabled={isDisabledForm()}
              />
            </div>
          </div>
          <div className="form__form-group">
            <span className="form__form-group-label">
              {t('mnt_medication.hour02')}
            </span>
            <div className="form__form-group-field">
              <Field
                name="hour02"
                type="text"
                component={HourField}
                placeholder={t('placeholder.hour')}
                isDisabled={isDisabledForm()}
              />
            </div>
          </div>
          <div className="form__form-group">
            <span className="form__form-group-label">
              {t('mnt_medication.hour03')}
            </span>
            <div className="form__form-group-field">
              <Field
                name="hour03"
                type="text"
                component={HourField}
                placeholder={t('placeholder.hour')}
                isDisabled={isDisabledForm()}
              />
            </div>
          </div>
          <div className="form__form-group">
            <span className="form__form-group-label">
              {t('mnt_medication.hour04')}
            </span>
            <div className="form__form-group-field">
              <Field
                name="hour04"
                type="text"
                component={HourField}
                placeholder={t('placeholder.hour')}
                isDisabled={isDisabledForm()}
              />
            </div>
          </div>
          <div className="form__form-group">
            <span className="form__form-group-label">
              {t('mnt_medication.hour05')}
            </span>
            <div className="form__form-group-field">
              <Field
                name="hour05"
                type="text"
                component={HourField}
                placeholder={t('placeholder.hour')}
                isDisabled={isDisabledForm()}
              />
            </div>
          </div>
        </div>
        <div className="form__form-group">
          <span className="form__form-group-label">
            {t('general.observations')}
          </span>
          <div className="form__form-group-field">
            <Field
              name="observation"
              type="text"
              component="textarea"
              maxLength="250"
              placeholder={t('general.observations')}
              disabled={isDisabledForm()}
            />
          </div>
        </div>
        <div className="form__form-group">
          <span className="form__form-group-label">
            {t('mnt_medication.recipe')}
          </span>
          <div className="form__form-group">
            <div className="form__form-group-field">
              <Field
                name="files"
                component={renderDropZoneField}
                disabled={isDisabledForm()}
              />
            </div>
          </div>
        </div>
        <div className="form__form-group">
          <span className="form__form-group-label">{t('general.history')}</span>
          <MatTable
            data={historicalData}
            icons={materialTableIcons}
            columns={ColumnsHistoric(t)}
            onMedicationViewRecipe={onRecipeItemClickHandler}
            actionsDisabled
            actionsDisabledByConditional={actionsDisabledByConditional}
            toolbar={false}
            pageSize={5}
          />
        </div>
        <ButtonToolbar>
          <Button color="primary" onClick={backHandler}>
            {t('general.back')}
          </Button>
          {!isDisabledForm() && (
            <Button color="success" type="submit">
              {t('general.save')}
            </Button>
          )}
        </ButtonToolbar>
      </form>
    </Fragment>
  );
};

MedicationForm.propTypes = {
  t: PropTypes.func.isRequired,
  backHandler: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  dto: PropTypes.shape(PropTypes.shape).isRequired,
  initialize: PropTypes.func.isRequired,
  showLoading: PropTypes.func.isRequired,
  hideLoading: PropTypes.func.isRequired,
  change: PropTypes.func.isRequired,
};
export default reduxForm({
  form: 'MedicationForm',
  validate,
  keepDirtyOnReinitialize: true,
  enableReinitialize: true,
})(withTranslation('common')(MedicationForm));
